import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaHandshake, FaSearch, FaPhone, FaMapMarkerAlt, FaPlus, FaGlobe, FaEnvelope, FaClock
} from 'react-icons/fa';
import './SheriffOffices.css'; // Reusing the same styles

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const BailAgencies = () => {
  const [agencies, setAgencies] = useState([]);
  const [filterOptions, setFilterOptions] = useState({
    counties: []
  });
  const [filters, setFilters] = useState({
    search: '',
    county: ''
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchFilterOptions();
    fetchAgencies();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchAgencies();
    }, 300);

    return () => clearTimeout(timer);
  }, [filters]);

  const fetchFilterOptions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/bail-agencies/filters/options`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setFilterOptions(data.data);
      }
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  };

  const fetchAgencies = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Build query string
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.county) params.append('county', filters.county);

      const response = await fetch(`${API_URL}/bail-agencies?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setAgencies(data.data);
        setError('');
      } else {
        setError('Failed to load bail agencies');
      }
    } catch (err) {
      console.error('Error fetching agencies:', err);
      setError('Failed to load bail agencies');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      county: ''
    });
  };

  const handleAgencyClick = (agencyId) => {
    navigate(`/address-book/bail-agencies/${agencyId}`);
  };

  if (loading && agencies.length === 0) {
    return (
      <div className="sheriff-offices-container">
        <div className="loading">Loading bail agencies...</div>
      </div>
    );
  }

  return (
    <div className="sheriff-offices-container">
      {/* Header */}
      <div className="sheriff-offices-header">
        <div className="header-left">
          <FaHandshake size={32} style={{ color: '#f39c12' }} />
          <div>
            <h1>Montana Bail Bond Agencies</h1>
            <p className="subtitle">{agencies.length} bail agencies found</p>
          </div>
        </div>
        <button className="btn-add-office" onClick={() => navigate('/address-book/bail-agencies/new')}>
          <FaPlus /> Add Agency
        </button>
      </div>

      {/* Filters */}
      <div className="sheriff-offices-filters">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by name, county, or city..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-row">
          <div className="filter-group">
            <label>County</label>
            <select
              value={filters.county}
              onChange={(e) => handleFilterChange('county', e.target.value)}
              className="filter-select"
            >
              <option value="">All Counties</option>
              {filterOptions.counties.map(county => (
                <option key={county} value={county}>{county}</option>
              ))}
            </select>
          </div>

          {(filters.search || filters.county) && (
            <button className="btn-clear-filters" onClick={clearFilters}>
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Agencies List */}
      <div className="offices-list">
        {agencies.length === 0 ? (
          <div className="no-results">
            <FaHandshake size={48} />
            <p>No bail agencies found matching your criteria</p>
          </div>
        ) : (
          agencies.map(agency => (
            <div
              key={agency.id}
              className="office-card"
              onClick={() => handleAgencyClick(agency.id)}
            >
              <div className="office-header">
                <div className="office-title">
                  <FaHandshake className="office-icon" style={{ color: '#f39c12' }} />
                  <div>
                    <h3>{agency.name}</h3>
                    {agency.dba && <p className="dba-text">DBA: {agency.dba}</p>}
                  </div>
                </div>
                <div className="header-badges">
                  {agency.county && <span className="county-badge">{agency.county} County</span>}
                  {agency.available_24_7 && (
                    <span className="badge-24-7" title="24/7 Service">
                      <FaClock /> 24/7
                    </span>
                  )}
                </div>
              </div>

              <div className="office-info">
                <div className="info-row">
                  {(agency.address_city || agency.city) && (
                    <div className="info-item">
                      <FaMapMarkerAlt className="info-icon" />
                      <span>{agency.address_city || agency.city}, MT</span>
                    </div>
                  )}

                  {agency.phone && (
                    <div className="info-item">
                      <FaPhone className="info-icon" />
                      <span>{agency.phone}</span>
                    </div>
                  )}

                  {agency.email && (
                    <div className="info-item">
                      <FaEnvelope className="info-icon" />
                      <span>{agency.email}</span>
                    </div>
                  )}

                  {agency.website && (
                    <div className="info-item">
                      <FaGlobe className="info-icon" />
                      <a
                        href={agency.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Website
                      </a>
                    </div>
                  )}
                </div>

                {agency.address_street && (
                  <div className="address-detail">
                    {agency.address_street}
                  </div>
                )}

                {agency.owner && (
                  <div className="owner-info">
                    <strong>Owner:</strong> {agency.owner}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default BailAgencies;
